import { useEffect, useMemo, useState } from 'react'
import {
    COMPONENT_JOB_SOURCE_EXTENSIONS,
    cancelRun,
    getComponentJobCompanionPaths,
    getComponentJobRootPath,
    getComponentRunPaths,
    getWorkspaceJobs,
    isComponentJobEntrypointPath,
    type ComponentRunStatusRecord,
} from '@secureblob/sdk/componentJobs'
import { useFileSystem, useRegistry, useWorkspaceMetadata } from '../hooks/useWorkspace'
import { formatExactTime, timeAgo } from '../utils/writerUtils'
import { useToast } from './Toast'
import { createComponentJobsClientOptions } from './componentJobs/apiClient'
import {
    createComponentJobRunSummaryFromStatusRecord,
    getComponentJobSourcePaths,
    isActiveComponentJobStatus,
    isQueuedComponentJobStatus,
    isRunningComponentJobStatus,
    loadComponentJobRunSummary,
    resolveComponentJobSourcePath,
    shortComponentJobArtifactId,
} from './componentJobs/jobHistory'
import {
    hasComponentJobCancelRequest,
    writeComponentJobCancelRequest,
} from './componentJobs/jobControl'
import './ComponentJobMiniWidget.css'

interface ComponentJobMiniWidgetProps {
    currentPath?: string | null
    onOpenFile?: (path: string) => void
}

type ComponentJobMiniTone = 'running' | 'queued'

interface ActiveComponentJobItem {
    sourcePath: string
    sourceName: string
    metadataPath: string
    runId: string
    startedAt: number
    target: string
    targetLabel: string
    status: string
    statusLabel: string
    engineLabel: string | null
    artifactLabel: string | null
    progressLabel: string
    tone: ComponentJobMiniTone
    cancelRequested: boolean
}

function formatTargetLabel(target: string): string {
    return target === 'local' ? 'Local' : 'Server'
}

function extractLatestProgressLine(text: string | null): string | null {
    if (typeof text !== 'string' || text.trim().length === 0) return null
    const lines = text
        .split(/\r?\n/)
        .map(line => line.trim())
        .filter(Boolean)
    const lastLine = lines.length > 0 ? lines[lines.length - 1] : null

    if (!lastLine) return null
    return lastLine.length > 160 ? `${lastLine.slice(0, 157)}...` : lastLine
}

function getActiveRunPriority(status: string): number {
    if (isRunningComponentJobStatus(status)) return 0
    if (isQueuedComponentJobStatus(status)) return 1
    return 2
}

function isServerCancelTarget(job: ActiveComponentJobItem, componentJobsClientAvailable: boolean): boolean {
    return componentJobsClientAvailable && job.target === 'server'
}

function mergeActiveRunSummary(
    sourcePath: string,
    localActiveRun: NonNullable<Awaited<ReturnType<typeof loadComponentJobRunSummary>>> | null,
    remoteActiveRuns: readonly ComponentRunStatusRecord[],
): NonNullable<Awaited<ReturnType<typeof loadComponentJobRunSummary>>> | null {
    const merged = new Map<string, NonNullable<Awaited<ReturnType<typeof loadComponentJobRunSummary>>>>()

    if (localActiveRun) {
        merged.set(localActiveRun.runId, localActiveRun)
    }

    for (const run of remoteActiveRuns) {
        const metadataPath = merged.get(run.runId)?.metadataPath ?? getComponentRunPaths(sourcePath, run.runId).metadata
        merged.set(run.runId, createComponentJobRunSummaryFromStatusRecord(run, metadataPath))
    }

    return [...merged.values()]
        .sort((left, right) => {
            const priorityDiff = getActiveRunPriority(left.status) - getActiveRunPriority(right.status)
            if (priorityDiff !== 0) return priorityDiff
            return right.startedAt - left.startedAt
        })[0] ?? null
}

function ActiveComponentJobCard({
    job,
    onOpenFile,
    onCancel,
}: {
    job: ActiveComponentJobItem
    onOpenFile?: (path: string) => void
    onCancel: (job: ActiveComponentJobItem) => void
}) {
    const cancelLabel = job.cancelRequested
        ? (job.target === 'local' ? 'Stopping...' : 'Canceling...')
        : (job.target === 'local' ? 'Stop' : 'Cancel')

    return (
        <article className={`component-job-mini-card ${job.tone}`}>
            <div className="component-job-mini-header">
                <div className={`component-job-mini-pulse ${job.tone}`} />
                <div className="component-job-mini-copy">
                    <span className="component-job-mini-kicker">{job.targetLabel} job</span>
                    <strong className="component-job-mini-name">{job.sourceName}</strong>
                    <span className="component-job-mini-path">{job.sourcePath}</span>
                </div>
                <span className={`component-job-mini-status ${job.tone}`}>{job.statusLabel}</span>
            </div>

            <p className="component-job-mini-progress">{job.progressLabel}</p>

            <div className="component-job-mini-footer">
                <div className="component-job-mini-meta">
                    <span title={formatExactTime(job.startedAt)}>{timeAgo(job.startedAt, { maxDepth: 'days' })}</span>
                    {job.engineLabel && <span>{job.engineLabel}</span>}
                    {job.artifactLabel && <span>{job.artifactLabel}</span>}
                </div>

                <div className="component-job-mini-actions">
                    <button
                        type="button"
                        className="component-job-mini-btn"
                        onClick={() => onOpenFile?.(job.sourcePath)}
                        disabled={!onOpenFile}
                    >
                        Open
                    </button>
                    <button
                        type="button"
                        className="component-job-mini-btn"
                        onClick={() => onCancel(job)}
                        disabled={job.cancelRequested}
                    >
                        {cancelLabel}
                    </button>
                </div>
            </div>
        </article>
    )
}

export default function ComponentJobMiniWidget({ currentPath = null, onOpenFile }: ComponentJobMiniWidgetProps) {
    const { files, readFile, writeFile, mkdir } = useFileSystem()
    const { entries } = useRegistry()
    const { blobId: workspaceBlobId } = useWorkspaceMetadata()
    const toast = useToast()
    const [activeJobs, setActiveJobs] = useState<ActiveComponentJobItem[]>([])

    const sourcePaths = useMemo(() => getComponentJobSourcePaths(files), [files])
    const fileSet = useMemo(() => new Set(files.filter(file => file.kind === 'file').map(file => file.path)), [files])
    const currentWorkspaceEntry = useMemo(
        () => entries.find(entry => entry.blobId === workspaceBlobId) ?? null,
        [entries, workspaceBlobId],
    )
    const componentJobsClient = useMemo(
        () => (currentWorkspaceEntry ? createComponentJobsClientOptions(currentWorkspaceEntry) : null),
        [currentWorkspaceEntry],
    )
    const currentSourcePath = useMemo(
        () => (currentPath ? resolveComponentJobSourcePath(currentPath, fileSet) : null),
        [currentPath, fileSet],
    )

    useEffect(() => {
        let cancelled = false

        const load = async () => {
            const fileSet = new Set(files.filter(file => file.kind === 'file').map(file => file.path))
            let remoteWorkspaceJobs: Awaited<ReturnType<typeof getWorkspaceJobs>> | null = null
            if (componentJobsClient) {
                try {
                    remoteWorkspaceJobs = await getWorkspaceJobs({
                        ...componentJobsClient,
                        workspaceBlobId,
                    })
                } catch {
                    remoteWorkspaceJobs = null
                }
            }

            // Build a local root map so we can remap remote source paths to local
            // paths when extensions differ (e.g. remote stored deploy.ts, local has deploy.tsx).
            // Without this, the mini widget would show duplicate cards and "Open"
            // would try to open a non-existent remote-only path.
            const localRootMap = new Map<string, string>()
            for (const sp of sourcePaths) {
                const root = getComponentJobRootPath(sp)
                if (root) {
                    localRootMap.set(root, sp)
                } else {
                    const ext = COMPONENT_JOB_SOURCE_EXTENSIONS.find(e => sp.endsWith(e))
                    if (ext) localRootMap.set(sp.slice(0, -ext.length), sp)
                }
            }

            function remapToLocalSourcePath(remotePath: string): string {
                const remoteRoot = getComponentJobRootPath(remotePath)
                if (remoteRoot) {
                    const localPath = localRootMap.get(remoteRoot)
                    if (localPath && localPath !== remotePath) return localPath
                } else {
                    const ext = COMPONENT_JOB_SOURCE_EXTENSIONS.find(e => remotePath.endsWith(e))
                    if (ext) {
                        const localPath = localRootMap.get(remotePath.slice(0, -ext.length))
                        if (localPath && localPath !== remotePath) return localPath
                    }
                }
                return remotePath
            }

            const remoteActiveRunsBySource = new Map<string, ComponentRunStatusRecord[]>()
            for (const run of remoteWorkspaceJobs?.runs ?? []) {
                if (!run.sourcePath || !isComponentJobEntrypointPath(run.sourcePath) || !isActiveComponentJobStatus(run.stage)) {
                    continue
                }

                const localPath = remapToLocalSourcePath(run.sourcePath)
                const current = remoteActiveRunsBySource.get(localPath) ?? []
                current.push(run)
                remoteActiveRunsBySource.set(localPath, current)
            }

            const candidateSourcePaths = [...new Set([
                ...sourcePaths,
                ...remoteActiveRunsBySource.keys(),
            ])]

            const nextJobs = await Promise.all(candidateSourcePaths.map(async sourcePath => {
                if (currentSourcePath === sourcePath) {
                    return null
                }

                const { runsDir } = getComponentJobCompanionPaths(sourcePath)
                const runMetadataFiles = files
                    .filter(file => file.kind === 'file' && file.path.startsWith(runsDir + '/') && file.path.endsWith('/run.json'))
                    .sort((left, right) => right.mtime - left.mtime)

                const runs = (await Promise.all(
                    runMetadataFiles.map(file => loadComponentJobRunSummary(file, readFile)),
                ))
                    .filter((item): item is NonNullable<Awaited<ReturnType<typeof loadComponentJobRunSummary>>> => item !== null)
                    .sort((left, right) => right.startedAt - left.startedAt)

                const activeRun = mergeActiveRunSummary(
                    sourcePath,
                    runs.find(run => isActiveComponentJobStatus(run.status)) ?? null,
                    remoteActiveRunsBySource.get(sourcePath) ?? [],
                )
                if (!activeRun) return null

                const runPaths = getComponentRunPaths(sourcePath, activeRun.runId)
                const latestStdoutLine = fileSet.has(runPaths.stdout)
                    ? extractLatestProgressLine(await readFile(runPaths.stdout))
                    : null

                return {
                    sourcePath,
                    sourceName: sourcePath.split('/').pop() ?? sourcePath,
                    metadataPath: activeRun.metadataPath,
                    runId: activeRun.runId,
                    startedAt: activeRun.startedAt,
                    target: activeRun.target,
                    targetLabel: formatTargetLabel(activeRun.target),
                    status: activeRun.status,
                    statusLabel: activeRun.statusLabel,
                    engineLabel: activeRun.engine ?? null,
                    artifactLabel: activeRun.artifactId ? `artifact ${shortComponentJobArtifactId(activeRun.artifactId)}` : null,
                    progressLabel: latestStdoutLine ?? activeRun.summary ?? `${formatTargetLabel(activeRun.target)} ${activeRun.statusLabel}`,
                    tone: isRunningComponentJobStatus(activeRun.status) ? 'running' : 'queued',
                    cancelRequested: hasComponentJobCancelRequest(fileSet, sourcePath, activeRun.runId),
                } satisfies ActiveComponentJobItem
            }))

            if (cancelled) return

            setActiveJobs(nextJobs
                .filter((job): job is ActiveComponentJobItem => job !== null)
                .sort((left, right) => {
                    const priorityDiff = getActiveRunPriority(left.status) - getActiveRunPriority(right.status)
                    if (priorityDiff !== 0) return priorityDiff
                    return right.startedAt - left.startedAt
                }))
        }

        load().catch(() => {
            if (!cancelled) {
                setActiveJobs([])
            }
        })

        return () => {
            cancelled = true
        }
    }, [componentJobsClient, currentSourcePath, fileSet, files, readFile, sourcePaths, workspaceBlobId])

    const handleCancel = (job: ActiveComponentJobItem) => {
        const request = async () => {
            if (isServerCancelTarget(job, !!componentJobsClient)) {
                await cancelRun({
                    ...componentJobsClient!,
                    runId: job.runId,
                })
            } else {
                writeComponentJobCancelRequest(job.sourcePath, job.runId, { writeFile, mkdir })
            }
        }

        void request()
            .then(() => {
                setActiveJobs(current => current.map(candidate => candidate.runId === job.runId
                    ? { ...candidate, cancelRequested: true }
                    : candidate))
                toast(`${job.target === 'local' ? 'Stop' : 'Cancel'} requested for ${job.sourceName}`, { type: 'success' })
            })
            .catch(error => {
                toast(
                    error instanceof Error
                        ? `Failed to request ${job.target === 'local' ? 'stop' : 'cancel'}: ${error.message}`
                        : `Failed to request ${job.target === 'local' ? 'stop' : 'cancel'}`,
                    { type: 'error' },
                )
            })
    }

    if (activeJobs.length === 0) return null

    return (
        <div className="component-job-mini-stack">
            {activeJobs.map(job => (
                <ActiveComponentJobCard
                    key={job.runId}
                    job={job}
                    onOpenFile={onOpenFile}
                    onCancel={handleCancel}
                />
            ))}
        </div>
    )
}
